import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { queryRecentlyViewedListings } from '../../ducks/user.duck';
import { listingCardIncludeParams } from '../../util/imageVariants';
import config from '../../config';
import { PAGE_TYPE_QUERY } from '../../util/listingType';

const LANDING_PAGE_LISTINGS_COUNT = 24;
const LANDING_PAGE_EXPERIENCES_COUNT = 9;

// ================ Action types ================ //

export const QUERY_LISTINGS_REQUEST = 'app/LandingPage/QUERY_LISTINGS_REQUEST';
export const QUERY_LISTINGS_SUCCESS = 'app/LandingPage/QUERY_LISTINGS_SUCCESS';
export const QUERY_LISTINGS_ERROR = 'app/LandingPage/QUERY_LISTINGS_ERROR';

export const QUERY_EXPERIENCES_REQUEST = 'app/LandingPage/QUERY_EXPERIENCES_REQUEST';
export const QUERY_EXPERIENCES_SUCCESS = 'app/LandingPage/QUERY_EXPERIENCES_SUCCESS';
export const QUERY_EXPERIENCES_ERROR = 'app/LandingPage/QUERY_EXPERIENCES_ERROR';

export const QUERY_WATCHLIST_LISTINGS_REQUEST = 'app/LandingPage/QUERY_WATCHLIST_LISTINGS_REQUEST';
export const QUERY_WATCHLIST_LISTINGS_SUCCESS = 'app/LandingPage/QUERY_WATCHLIST_LISTINGS_SUCCESS';
export const QUERY_WATCHLIST_LISTINGS_ERROR = 'app/LandingPage/QUERY_WATCHLIST_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  listingIds: [],
  queryListingsInProgress: false,
  queryListingsError: null,
  liveRoomIds: [],
  queryLiveRoomsInProgress: false,
  queryLiveRoomsError: null,
  watchlistListingIds: [],
  queryWatchlistListings: false,
  queryWatchlistListingsError: null,
};

const resultIds = data => data.data.map(l => l.id);

export default function landingPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case QUERY_LISTINGS_REQUEST:
      return {
        ...state,
        queryListingsInProgress: true,
        queryListingsError: null,
      };
    case QUERY_LISTINGS_SUCCESS:
      return {
        ...state,
        queryListingsInProgress: false,
        queryListingsError: null,
        listingIds: resultIds(payload.data),
      };
    case QUERY_LISTINGS_ERROR:
      return { ...state, queryListingsInProgress: false, queryListingsError: payload };

    case QUERY_EXPERIENCES_REQUEST:
      return {
        ...state,
        queryLiveRoomsInProgress: true,
        queryLiveRoomsError: null,
      };
    case QUERY_EXPERIENCES_SUCCESS:
      return {
        ...state,
        queryLiveRoomsInProgress: false,
        queryLiveRoomsError: null,
        liveRoomIds: resultIds(payload.data),
      };
    case QUERY_EXPERIENCES_ERROR:
      return { ...state, queryLiveRoomsInProgress: false, queryLiveRoomsError: payload };

    case QUERY_WATCHLIST_LISTINGS_REQUEST:
      return {
        ...state,
        queryWatchlistListingsInProgress: true,
        querWatchlistlistingssError: null,
      };
    case QUERY_WATCHLIST_LISTINGS_SUCCESS:
      return {
        ...state,
        queryWatchlistListingsInProgress: false,
        queryWatchlistListingsError: null,
        watchlistListingIds: resultIds(payload.data),
      };
    case QUERY_WATCHLIST_LISTINGS_ERROR:
      return {
        ...state,
        queryWatchlistListingsInProgress: false,
        queryWatchlistListingsError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const queryListingsRequest = () => ({
  type: QUERY_LISTINGS_REQUEST,
});

export const queryListingsSuccess = response => ({
  type: QUERY_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const queryListingsError = e => ({
  type: QUERY_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const queryExperiencesRequest = () => ({
  type: QUERY_EXPERIENCES_REQUEST,
});

export const queryExperiencesSuccess = response => ({
  type: QUERY_EXPERIENCES_SUCCESS,
  payload: { data: response.data },
});

export const queryLiveRoomsError = e => ({
  type: QUERY_EXPERIENCES_ERROR,
  error: true,
  payload: e,
});

export const queryWatchlistListingsRequest = () => ({
  type: QUERY_WATCHLIST_LISTINGS_REQUEST,
});

export const queryWatchlistListingsSuccess = response => ({
  type: QUERY_WATCHLIST_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const queryWatchlistListingsError = e => ({
  type: QUERY_WATCHLIST_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const queryListings = () => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest());
  return sdk.listings
    .query({
      pub_type: PAGE_TYPE_QUERY,
      perPage: LANDING_PAGE_LISTINGS_COUNT,
      ...listingCardIncludeParams(),
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(queryListingsSuccess(response));

      return response;
    })
    .catch(e => dispatch(queryListingsError(storableError(e))));
};

export const queryExperiences = () => (dispatch, getState, sdk) => {
  dispatch(queryExperiencesRequest());
  return sdk.listings
    .query({
      pub_type: config.listingTypes['live-room'],
      perPage: LANDING_PAGE_EXPERIENCES_COUNT,
      ...listingCardIncludeParams(),
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(queryExperiencesSuccess(response));

      return response;
    })
    .catch(e => dispatch(queryLiveRoomsError(storableError(e))));
};

export const queryWatchlistListings = () => async (dispatch, getState, sdk) => {
  try {
    dispatch(queryWatchlistListingsRequest());

    // A visitor who isn't signed in has no watchlist: asking for the current user would only
    // get a 403, and both calls are billed.
    if (!getState().Auth?.isAuthenticated) {
      dispatch(queryWatchlistListingsSuccess({ data: { data: [] } }));
      return null;
    }

    let { currentUser } = getState().user;

    if (!currentUser) {
      const currentUserResponse = await sdk.currentUser.show();
      currentUser = currentUserResponse?.data?.data;
    }

    const watchlistIds = currentUser?.attributes?.profile?.publicData?.watchlistIds || [];
    if (watchlistIds.length === 0) {
      dispatch(queryWatchlistListingsSuccess({ data: { data: [] } }));
      return null;
    }

    const response = await sdk.listings.query({
      ids: watchlistIds,
      ...listingCardIncludeParams(),
    });

    dispatch(addMarketplaceEntities(response));
    dispatch(queryWatchlistListingsSuccess(response));

    return response;
  } catch (error) {
    dispatch(queryWatchlistListingsError(storableError(error)));
  }
};

export const loadData = () => async (dispatch, getState, sdk) => {
  // On SSR there's no auth cookie and no localStorage, so user-specific
  // queries either fail or return empty. Skip them server-side to keep
  // SSR TTFB fast — the client will dispatch them after hydration.
  const isServer = typeof window === 'undefined';
  const publicCalls = [dispatch(queryListings()), dispatch(queryExperiences())];
  const userCalls = isServer
    ? []
    : [dispatch(queryWatchlistListings()), dispatch(queryRecentlyViewedListings())];
  return Promise.all([...publicCalls, ...userCalls]);
};

// What the browser still has to load when it skips loadData after server
// rendering (see onlyClientDataNeeded in src/Routes.js): the parts loadData
// leaves out on the server.
export const loadClientData = () => dispatch =>
  Promise.all([dispatch(queryWatchlistListings()), dispatch(queryRecentlyViewedListings())]);
